/* jshint esversion: 6 */
/* jshint ignore:start */
import { useEffect, useRef, useState } from "react";
import { showToast } from "../showTost";

function loadCashfreeSdk() {
  return new Promise((resolve, reject) => {
    if (window.Cashfree) {
      resolve(window.Cashfree);
      return;
    }
    const existing = document.getElementById("cashfree-js-sdk");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Cashfree));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.id = "cashfree-js-sdk";
    script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    script.async = true;
    script.onload = () => resolve(window.Cashfree);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

/**
 * Generic Cashfree flow for v1 checkouts (pharmacy/lab):
 * createCheckout() -> { payment_session_id, cashfree_order_id, ref, environment }
 * confirmPayment({ cashfree_order_id, ref }) -> confirms after modal success.
 * The server re-verifies with Cashfree, and the webhook confirms as backup.
 */
const V1CashfreeCheckout = ({ amount, createCheckout, confirmPayment, onSuccess, onCancel }) => {
  const started = useRef(false);
  const [phase, setPhase] = useState("loading"); // loading | open | verifying | error | done
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    let cancelled = false;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const run = async () => {
      setPhase("loading");
      let session;
      try {
        session = await createCheckout();
      } catch (err) {
        if (cancelled) return;
        setPhase("error");
        setErrorMsg(err?.response?.data?.error_message || err.message || "Could not start online checkout");
        showToast({ title: err?.response?.data?.error_message || "Could not start online checkout", id: "error" });
        return;
      }
      if (cancelled) return;
      if (!session?.payment_session_id) {
        setPhase("error");
        setErrorMsg("Online checkout could not start");
        return;
      }
      try {
        const Cashfree = await loadCashfreeSdk();
        const mode = session.environment === "production" ? "production" : "sandbox";
        const cashfree = Cashfree({ mode });
        setPhase("open");
        const result = await cashfree.checkout({
          paymentSessionId: session.payment_session_id,
          redirectTarget: "_modal",
        });
        if (cancelled) return;
        if (result?.error) {
          setPhase("error");
          setErrorMsg(result.error.message || "Checkout closed");
          showToast({ title: result.error.message || "Payment closed", id: "info" });
          return;
        }
        if (result?.paymentDetails) {
          setPhase("verifying");
          try {
            await confirmPayment({ cashfree_order_id: session.cashfree_order_id, ref: session.ref });
            if (cancelled) return;
            setPhase("done");
            onSuccess(session.ref);
          } catch (cerr) {
            if (cancelled) return;
            setPhase("error");
            const msg = cerr?.response?.data?.error_message || "Payment done but confirmation failed. It will reflect shortly.";
            setErrorMsg(msg);
            showToast({ title: msg, id: "warning" });
          }
        } else if (!result?.redirect) {
          setPhase("error");
          setErrorMsg("Payment was not completed");
        } else {
          setPhase("open");
        }
      } catch (err) {
        console.error("Cashfree checkout error:", err);
        if (cancelled) return;
        setPhase("error");
        setErrorMsg("Cashfree checkout failed to open");
      }
    };

    run();
    return () => {
      cancelled = true;
      document.body.style.overflow = prevOverflow || "auto";
    };
  }, []);

  const showOverlay = phase === "loading" || phase === "open" || phase === "verifying";

  return (
    <>
      {showOverlay && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/55 p-4" role="dialog" aria-modal="true" aria-label="Secure payment">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md px-6 py-8 text-center">
            <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-[#0d9488] border-t-transparent animate-spin" />
            <h3 className="text-lg font-semibold text-gray-900">
              {phase === "verifying" ? "Confirming payment…" : "Secure payment"}
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              {phase === "verifying"
                ? "Please wait while we verify your payment."
                : "Cashfree checkout is open in the popup. Complete payment there — you stay on RapidMediK."}
            </p>
            <p className="text-xs text-gray-400 mt-4">Amount · ₹{amount}</p>
            <button type="button" className="mt-4 text-sm text-gray-500 underline" onClick={onCancel}>Cancel</button>
          </div>
        </div>
      )}
      {phase === "error" && (
        <div className="text-center py-4">
          <p className="text-sm text-red-600 mb-3">{errorMsg || "Payment error"}</p>
          <button type="button" className="text-sm text-rm-primary underline" onClick={onCancel}>← Back to payment options</button>
        </div>
      )}
      {phase === "done" && (
        <p className="text-sm text-center text-green-600 py-4">Payment successful</p>
      )}
    </>
  );
};

export default V1CashfreeCheckout;
/* jshint ignore:end */
